import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';

const URL = '/io/api/v1';

/** One status change NRVE recorded for a document. */
export interface IoDocumentEvent {
  status: string;
  at: string;
  eventId?: string;
}

export interface IoDocument {
  documentId: string;
  messageTitle?: string;
  status?: string;
  createdDate?: number;
  activityDate?: number;
  labels?: string[];
  /* Supplied by NRVE, not the provider: a replaced document keeps whatever
     status it ended on, so this is the only way to tell a superseded revision
     from the one that replaced it. */
  supersededAt?: string;
  supersededBy?: string;
  version?: number;
  /** This campaign books only against a signed client order (Prisma /
   *  DocuSign), so its line items are held back until one is attached. */
  requiresClientOrder?: boolean;
  /** 'awaiting' while the order is outstanding, 'received' once attached. */
  clientOrderStatus?: string;
  /** Name of the attached signed order, once there is one. */
  clientOrderFileName?: string;
  /* Commercial detail from the media plan, so a document can be recognised by
     what it books rather than only by its title — and so a superseded revision
     still shows its own figures. The signing provider knows none of this. */
  totalBudget?: number;
  currency?: string;
  startDate?: string;
  endDate?: string;
  campaignName?: string;
  advertiserName?: string;
  /** PipeDrive deal the plan was created from. */
  dealId?: number;
}

/** An address an IO can be sent from, as the provider will honour it. */
export interface IoSender {
  email: string;
  name: string;
}

export interface IoSendersResponse {
  senders: IoSender[];
}

export interface IoDocumentListResponse {
  pageDetails?: { page: number; pageSize: number; totalRecordsCount: number };
  result?: IoDocument[];
}

export interface RaiseIoRequest {
  mediaPlanId: string;
  templateId?: string;
  name?: string;
  recipient: { email: string; first_name?: string; last_name?: string };
  /** Skyrise signatory who counter-signs after the client. */
  countersigner?: { email: string; name?: string };
  columns?: unknown;
  pricing?: unknown;
  /** Send for signature rather than leaving the document undelivered. */
  send?: boolean;
  /** Extra addresses copied on this IO, on top of the standing list. */
  ccEmails?: string[];
  /** Address to send from, when the raiser chose one other than their own. */
  sendAsEmail?: string;
  /**
   * Raise this IO as a replacement for the plan's current live one.
   *
   * Without it the backend refuses with 409 when an IO is already live, so a
   * duplicate cannot be raised by accident.
   */
  replaceExisting?: boolean;
  /**
   * Raise the revision without sending it to the client.
   *
   * For a change the client has no part in — moving budget between line items
   * to follow delivery. The booking moves; nobody is asked to sign a document
   * for something they did not request.
   */
  internalOnly?: boolean;
}

/** One IO revision raised against a media plan. */
export interface PlanIoVersion {
  id?: string;
  name?: string;
  status?: string;
  version: number;
  raisedAt?: string;
  completedAt?: string;
  supersededAt?: string;
  supersededBy?: string;
  /** This revision currently holds the plan. */
  live: boolean;
  /** Every party has signed it. */
  signed: boolean;
  history?: IoDocumentEvent[];
}

export interface PlanIoVersions {
  mediaPlanId: string;
  /** Revision a new raise would create. */
  nextVersion: number;
  /** True when raising now needs `replaceExisting`. */
  hasLiveIo: boolean;
  /** Resolved from the buying point, so the raise screen can present the
   *  client-order question already answered rather than always clear. */
  requiresClientOrder?: boolean;
  /**
   * The signed IO no longer matches the plan's budget.
   *
   * Set when a deal's value moved in PipeDrive after its IO was signed. The
   * booking stands on what was signed; this says the document and the deal
   * have since parted company.
   */
  budgetChangedAfterSigning?: {
    signedBudget?: number;
    currentBudget?: number;
    at?: string;
    ioDocumentId?: string;
    currency?: string;
  };
  live?: PlanIoVersion;
  /** Every revision, oldest first. */
  versions: PlanIoVersion[];
}

/** Shape of the 409 the backend returns when an IO is already live. */
export interface LiveIoConflict {
  message?: string;
  existing?: {
    id?: string;
    name?: string;
    status?: string;
    version?: number;
    raisedAt?: string;
    signed?: boolean;
  };
  hint?: string;
}

/**
 * Insertion Orders: rendered by NRVE and sent for signature.
 *
 * Document layout lives in NRVE, so these endpoints only raise, track and
 * download — nothing here decides how the document looks.
 */
export const pandaDocApi = createApi({
  reducerPath: 'ioApi',
  baseQuery,
  tagTypes: ['IoDocuments'],
  endpoints: (builder) => ({
    raiseIo: builder.mutation<{ documentId: string }, RaiseIoRequest>({
      query: (body) => ({ url: `${URL}/raise-io`, method: 'POST', body }),
      invalidatesTags: ['IoDocuments']
    }),
    /* Drives the "an IO already exists" indication on the raise screen, so the
       user sees it before filling the form rather than through a 409 after. */
    getPlanIoVersions: builder.query<PlanIoVersions, string>({
      query: (mediaPlanId) => `${URL}/media-plans/${mediaPlanId}/io-versions`,
      providesTags: ['IoDocuments']
    }),
    /* Only identities the provider will actually send as — a pending one would
       be silently ignored and the document would go from the account. */
    listIoSenders: builder.query<IoSendersResponse, void>({
      query: () => `${URL}/senders`
    }),
    listDocuments: builder.query<IoDocumentListResponse, { page?: number; pageSize?: number } | void>({
      query: (params) => ({
        url: `${URL}/documents`,
        params: params ? { page: params.page, pageSize: params.pageSize } : undefined
      }),
      providesTags: ['IoDocuments']
    }),
    getDocument: builder.query<IoDocument, string>({
      query: (documentId) => `${URL}/documents/${documentId}`
    }),
    getDocumentHistory: builder.query<IoDocumentEvent[], string>({
      query: (documentId) => `${URL}/documents/${documentId}/history`
    }),
    downloadDocument: builder.mutation<Blob, string>({
      query: (documentId) => ({
        url: `${URL}/documents/${documentId}/download`,
        responseHandler: (response) => response.blob()
      })
    }),
    revokeDocument: builder.mutation<{ success: boolean }, { documentId: string; message: string }>({
      /* The reason travels to the provider, which shows it to the signers, so
         it is part of the request rather than a local note. */
      query: ({ documentId, message }) => ({
        url: `${URL}/documents/${documentId}/revoke`,
        method: 'POST',
        body: { message }
      }),
      invalidatesTags: ['IoDocuments']
    }),
    updatePoNumber: builder.mutation<
      { synced: boolean },
      { mediaPlanId: string; poNumber: string; lineItemId?: string }
    >({
      query: ({ mediaPlanId, ...body }) => ({
        url: `${URL}/media-plans/${mediaPlanId}/po-number`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: ['IoDocuments']
    }),
    /**
     * Records whether a campaign is booked through Prisma, and confirms the
     * Prisma order once it arrives.
     *
     * `released` reports whether booking rows were regenerated — false for a
     * plan whose IO is not yet signed, which has nothing to release yet.
     */
    updatePrismaStatus: builder.mutation<
      { released: boolean },
      {
        mediaPlanId: string;
        requiresPrisma?: boolean;
        prismaStatus?: 'awaiting' | 'received';
        prismaReference?: string;
      }
    >({
      query: ({ mediaPlanId, ...body }) => ({
        url: `${URL}/media-plans/${mediaPlanId}/prisma`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: ['IoDocuments']
    }),
    /**
     * Uploads the Prisma PDF and, by default, confirms the order received —
     * which releases the campaign's line items to the booking sheet.
     *
     * Sent as multipart, so `FormData` rather than a JSON body.
     */
    uploadPrismaFile: builder.mutation<
      { fileName: string; url: string; released: boolean },
      { mediaPlanId: string; file: File; prismaReference?: string; confirmReceived?: boolean }
    >({
      query: ({ mediaPlanId, file, prismaReference, confirmReceived }) => {
        const body = new FormData();
        body.append('file', file);
        if (prismaReference) body.append('prismaReference', prismaReference);
        if (confirmReceived === false) body.append('confirmReceived', 'false');
        return { url: `${URL}/media-plans/${mediaPlanId}/prisma/file`, method: 'POST', body };
      },
      invalidatesTags: ['IoDocuments']
    }),
    /** A fresh signed link to an already-uploaded Prisma document. */
    getPrismaFileLink: builder.query<{ fileName?: string; url: string }, string>({
      query: (mediaPlanId) => `${URL}/media-plans/${mediaPlanId}/prisma/file`
    })
  })
});

export const {
  useRaiseIoMutation,
  useGetPlanIoVersionsQuery,
  useListDocumentsQuery,
  useListIoSendersQuery,
  useLazyGetDocumentQuery,
  useGetDocumentHistoryQuery,
  useDownloadDocumentMutation,
  useRevokeDocumentMutation,
  useUpdatePoNumberMutation,
  useUpdatePrismaStatusMutation,
  useUploadPrismaFileMutation,
  useLazyGetPrismaFileLinkQuery
} = pandaDocApi;
